import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getUserFromToken } from "@core/index";
import { Can } from "@core/casl-provider";
import { Box, IconButton, Stack } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import { FormikHelpers } from "formik";
import { enqueueSnackbar } from "notistack";
import { CustomDataGrid } from "@shared/components";
import { formatDateTime } from "@shared/utils/functions";
import { useSortModel } from "@shared/components/grid-components/hooks/useSortModel";
import { useGetALlRolesList } from "../services/useGetAllRolesList";
import { useGetAllUsersList } from "../services/useGetAllUsersList";
import { useUpdateRole } from "../services/useUpdateRole";
import { AllowedSortFields } from "../utils/constants";
import {
  IRoleManagementRow,
  RoleManagementDataGridProps,
  RoleManagementFormValues,
  SortField,
} from "../utils/types";
import RoleManagementForm from "./RoleManagementForm";
import { EditOutlined } from "@mui/icons-material";

const RoleManagementGrid = ({
  visibleColumns,
  setPaginationModel,
  paginationModel,
}: RoleManagementDataGridProps) => {
  const user = getUserFromToken();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("searchQuery") ?? "";
  const { sortModel, handleSortModelChange, orderBy, order } =
    useSortModel<SortField>({
      allowedSortFields: AllowedSortFields,
    });

  const { data: allUsersListResponse, isLoading } = useGetAllUsersList({
    data: {
      pageNumber: String(paginationModel.page + 1),
      pageSize: String(paginationModel.pageSize),
      searchQuery: searchQuery,
      orderBy: orderBy,
      order: order,
    },
  });
  const { data: allRolesListResponse } = useGetALlRolesList();
  const { mutateAsync: updateRoleMutationAsync } = useUpdateRole();

  const [actionState, setActionState] = useState<{
    action: "EDIT" | "DELETE" | "";
    payload: Partial<IRoleManagementRow>;
  }>({
    action: "",
    payload: {},
  });

  const [allUsersLists, setAllUsersLists] = useState<IRoleManagementRow[]>();
  const [rolesOptions, setRolesOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const totalRows: number =
    (allUsersListResponse?.pagination?.total as number) ??
    allUsersLists?.length ??
    0;
  const clearActionState = () => setActionState({ action: "", payload: {} });
  const handleEditSubmit = async (
    values: RoleManagementFormValues & { id?: number },
    helpers: FormikHelpers<RoleManagementFormValues & { id?: number }>
  ) => {
    try {
      const id = actionState.payload.id;
      const payloadData = { role: values.role };

      await updateRoleMutationAsync({
        payload: { id: id as number, payloadData },
      });
      clearActionState();
    } catch (error: unknown) {
      const errMsg: string =
        error instanceof Error
          ? error.message
          : "Failed to update configuration";
      enqueueSnackbar(errMsg, {
        variant: "error",
        autoHideDuration: 5000,
        preventDuplicate: true,
      });
    } finally {
      helpers.setSubmitting(false);
    }
  };

  useEffect(() => {
    if (allUsersListResponse?.data) {
      const data = allUsersListResponse.data.map((item) => ({
        ...item,
      }));
      setAllUsersLists(data as IRoleManagementRow[]);
    }
  }, [allUsersListResponse]);

  useEffect(() => {
    if (allRolesListResponse?.roles) {
      setRolesOptions(
        allRolesListResponse?.roles as { label: string; value: string }[]
      );
    }
  }, [allRolesListResponse]);

  const RoleManagementTableActions = [
    {
      key: "EDIT",
      label: "Edit",
      icon: (
        <EditOutlined
          fontSize="small"
          sx={(theme) => ({
            color:
              theme.palette.mode === "dark"
                ? theme.palette.common.white
                : theme.palette.grey[500],
          })}
        />
      ),
    },
  ] as const;

  const columns: GridColDef<IRoleManagementRow>[] = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.2,
      minWidth: 120,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<IRoleManagementRow, number>
      ) => <span>{params.value}</span>,
    },
    {
      field: "googleId",
      headerName: "Google ID",
      flex: 0.2,
      minWidth: 160,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<IRoleManagementRow, string>
      ) => <span>{params.value}</span>,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (
        params: GridRenderCellParams<IRoleManagementRow, string>
      ) => <span style={{ textTransform: "capitalize" }}>{params.value}</span>,
    },

    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 180,
      sortable: true,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.2,
      minWidth: 120,
      sortable: true,
      renderCell: (
        params: GridRenderCellParams<IRoleManagementRow, string>
      ) => <span style={{ textTransform: "capitalize" }}>{params.value}</span>,
    },

    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      minWidth: 210,
      sortable: true,
      renderCell: (params: GridRenderCellParams<IRoleManagementRow, string>) =>
        formatDateTime(params.value),
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      renderCell: (
        params: GridRenderCellParams<
          IRoleManagementRow,
          unknown,
          unknown,
          GridTreeNodeWithRender
        >
      ) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems={"center"}
          justifyContent={"center"}
          sx={{ height: "100%" }}
        >
          {RoleManagementTableActions.map((action, index) => (
            <Can
              do="update"
              on="RoleManagement"
              key={index}
            >
              <IconButton
                title={action.label}
                style={{
                  backgroundColor: "primary.main",
                  opacity: params.row.googleId === user?.googleId ? 0.5 : 1,
                }}
                disabled={params.row.googleId === user?.googleId}
                onClick={() =>
                  setActionState({
                    action: action.key,
                    payload: {
                      id: params.row.id,
                      name: params.row.name,
                      email: params.row.email,
                      role: params.row.role,
                      googleId: params.row.googleId,
                      updatedAt: params.row.updatedAt,
                    },
                  })
                }
              >
                {action.icon}
              </IconButton>
            </Can>
          ))}
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          borderBottomLeftRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
          overflow: "hidden",
        })}
      >
        <CustomDataGrid<IRoleManagementRow>
          rows={allUsersLists}
          columns={columns}
          visibleColumns={visibleColumns}
          getRowId={(row) => row.id}
          rowCount={totalRows}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          autoHeight
          disableSelectionOnClick
          loading={isLoading}
          dataGridSx={(theme) => ({
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          })}
        />
      </Box>

      <RoleManagementForm
        open={actionState.action === "EDIT"}
        onClose={clearActionState}
        title={"Edit Role Management"}
        initialValues={actionState.payload}
        // disable={isFormPending}
        onSubmit={handleEditSubmit}
        showCloseButton
        rolesOptions={rolesOptions}
      />
    </>
  );
};

export default RoleManagementGrid;
